import { useEffect, useState } from 'react';
import { api, audioUrl, type Message } from '../api.js';
import Icon from '../components/Icon.js';
import AudioPlayer from '../components/AudioPlayer.js';

const VOICES = ['Kore', 'Aoede', 'Charon', 'Fenrir', 'Leda', 'Orus', 'Puck', 'Zephyr'];

export default function Messages() {
  const [items, setItems] = useState<Message[]>([]);
  const [text, setText] = useState('');
  const [lang, setLang] = useState('en-GB');
  const [voice, setVoice] = useState('Kore');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [silenceSec, setSilenceSec] = useState(60);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');

  const load = () => api.messages().then(setItems).catch((e) => setErr(e.message));
  useEffect(() => { void load(); }, []);

  async function generate(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setErr(''); setNote('');
    try {
      const m = await api.createMessage({ name, text, lang, voice });
      setNote(m.cached
        ? 'An identical message already exists — reused, nothing was generated.'
        : `Generated: ${((m.durationMs ?? 0) / 1000).toFixed(1)}s, automatic hangup at ${m.maxTalkSeconds}s.`);
      setText('');
      await load();
    } catch (e) { setErr(e instanceof Error ? e.message : 'error'); }
    finally { setBusy(false); }
  }

  const seconds = (ms: number | null) => ms ? (ms / 1000).toFixed(1) + 's' : '';

  async function remove(m: Message) {
    if (!confirm(`Delete the message "${m.name}"?`)) return;
    setErr(''); setNote('');
    try { await api.deleteMessage(m.id); setNote(`"${m.name}" was deleted.`); await load(); }
    catch (e) { setErr(e instanceof Error ? e.message : 'error'); }
  }

  async function makeSilence() {
    setBusy(true); setErr(''); setNote('');
    try {
      const m = await api.createSilence(silenceSec);
      setNote(m.cached
        ? 'A silence file of the same length already exists — reused.'
        : `${silenceSec}s of silence ready. The call hangs up automatically at ${m.maxTalkSeconds}s.`);
      await load();
    } catch (e) { setErr(e instanceof Error ? e.message : 'error'); }
    finally { setBusy(false); }
  }

  return (
    <>
      <h1>Messages</h1>
      <p className="subtitle">
        The text is written by the campaign operator. The cost of the call is driven
        almost entirely by its length — ringing is not billed.
      </p>

      {err && <div className="err">{err}</div>}

      <div className="card" style={{ marginBottom: 22 }}>
        <form onSubmit={generate}>
          <div className="grid3">
            <label><span>Name (optional)</span>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. UK network test" /></label>
            <label><span>Language (BCP-47, independent of country)</span>
              <input value={lang} onChange={(e) => setLang(e.target.value)} placeholder="en-GB" /></label>
            <label><span>Voice</span>
              <select value={voice} onChange={(e) => setVoice(e.target.value)}>
                {VOICES.map((v) => <option key={v}>{v}</option>)}
              </select></label>
          </div>
          <label><span>Text</span>
            <textarea value={text} onChange={(e) => setText(e.target.value)}
                      placeholder="What is heard on the call…" /></label>
          <div className="row">
            <button className="primary" disabled={busy || !text.trim()}>
              {busy ? 'Generating…' : 'Generate'}
            </button>
            {note && <span className="muted">{note}</span>}
          </div>
        </form>
      </div>

      <div className="card" style={{ marginBottom: 22 }}>
        <h2 style={{ marginTop: 0 }}>Call without a message — listen only</h2>
        <p className="muted" style={{ fontSize: 12, marginTop: -4 }}>
          The platform says nothing; silence is played, and the recording captures what
          the other party says. That is how you hear whether a human, a voicemail box or
          a network announcement answered — something the SIP codes do not tell you.
        </p>
        <div className="row">
          <label style={{ margin: 0, width: 160 }}><span>Duration (seconds)</span>
            <input type="number" min={5} max={300} value={silenceSec}
                   onChange={(e) => setSilenceSec(Number(e.target.value))} /></label>
          <button disabled={busy} onClick={() => void makeSilence()}>
            <Icon name="plus" />Add to the library
          </button>
        </div>
      </div>

      {items.length === 0 ? <div className="card empty">The library is empty.</div> : (
        <div className="table-wrap">
          <table>
            <thead><tr>
              <th>Name</th><th>Language</th><th>Voice</th><th>Duration</th><th>Listen</th><th>Text</th><th></th>
            </tr></thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id}>
                  <td>{m.name}</td>
                  <td className="num">{m.voice === 'silence'
                    ? <span className="pill">no message</span> : m.lang}</td>
                  <td className="num">{m.voice === 'silence' ? '—' : m.voice}</td>
                  <td className="num">{seconds(m.durationMs)}</td>
                  <td><AudioPlayer src={audioUrl.message(m.sha256)} /></td>
                  <td className="wrap">{m.text}</td>
                  <td>
                    <button className="icon-only danger" title="Delete the message"
                            onClick={() => void remove(m)}><Icon name="trash" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}
